import { useSubscription } from '@/context/SubscriptionContext';
import { billingAPI } from '@/lib/api';
import { toast } from 'sonner';
import { WarningCircle, ArrowRight } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';

export const SubscriptionBanner = () => {
  const { subscription, billingStatus, isReadOnly, isPlatformAdmin } = useSubscription();
  const navigate = useNavigate();

  // Platform admins never see subscription banners
  if (isPlatformAdmin) return null;

  if (!isReadOnly) {
    // Show trial warning if < 4 days left
    if (billingStatus?.status === 'trialing' && billingStatus?.trial_days_remaining <= 4) {
      return (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2.5 flex items-center justify-between" data-testid="trial-warning-banner">
          <div className="flex items-center gap-2 text-sm text-amber-800">
            <WarningCircle size={18} weight="fill" />
            <span>Your trial ends in <strong>{billingStatus.trial_days_remaining} day{billingStatus.trial_days_remaining !== 1 ? 's' : ''}</strong>. Choose a plan to keep your data.</span>
          </div>
          <button
            onClick={() => navigate('/settings?tab=subscription')}
            className="text-sm font-medium text-amber-900 hover:text-amber-700 flex items-center gap-1"
            data-testid="trial-warning-cta"
          >
            Choose Plan <ArrowRight size={14} />
          </button>
        </div>
      );
    }
    return null;
  }

  const status = subscription?.status;
  let message = 'Your subscription has expired. Choose a plan to continue.';
  let bgClass = 'bg-red-50 border-red-200';
  let textClass = 'text-red-800';

  if (status === 'past_due') {
    message = 'Your payment failed. Update your payment method to avoid losing access.';
    bgClass = 'bg-amber-50 border-amber-200';
    textClass = 'text-amber-800';
  } else if (status === 'cancelled') {
    message = 'Your subscription was cancelled. Reactivate to regain full access.';
  }

  const handleUpgrade = () => {
    navigate('/settings?tab=subscription');
  };

  return (
    <div className={`${bgClass} border-b px-4 py-3 flex items-center justify-between`} data-testid="subscription-expired-banner">
      <div className={`flex items-center gap-2 text-sm ${textClass}`}>
        <WarningCircle size={18} weight="fill" />
        <span>{message}</span>
      </div>
      <button
        onClick={handleUpgrade}
        className={`text-sm font-semibold ${textClass} hover:opacity-80 flex items-center gap-1 px-3 py-1 rounded-lg border border-current`}
        data-testid="subscription-upgrade-cta"
      >
        Choose a Plan <ArrowRight size={14} />
      </button>
    </div>
  );
};
